import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, logAdminActivity } from '../../lib/firebase';
import { X, MessageCircle, RefreshCw, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateImageDescription } from '../../lib/openai';

interface Tree {
  id: string;
  name: string;
  creator: string;
  email: string;
  imageUrl: string;
  votes: number;
  views: number;
  status: 'pending' | 'approved' | 'rejected';
  description?: string;
}

interface EditTreeModalProps {
  tree: Tree;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditTreeModal({ tree, onClose, onSuccess }: EditTreeModalProps) {
  const [formData, setFormData] = useState({
    name: tree.name,
    creator: tree.creator,
    email: tree.email,
    votes: tree.votes,
    views: tree.views || 0,
    status: tree.status,
    description: tree.description || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'votes' || name === 'views' ? parseInt(value) || 0 : value
    }));
  };

  const handleGenerateDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'ai-settings'));
      const prompt = settingsDoc.exists() ? settingsDoc.data()?.descriptionPrompt : '';

      if (!prompt) {
        toast.error('No hay un prompt configurado para la IA');
        return;
      }

      const description = await generateImageDescription(tree.imageUrl, prompt);
      if (description) {
        setFormData(prev => ({ ...prev, description }));
        toast.success('Descripción generada con éxito');
      }
    } catch (error: any) {
      console.error('Error generando descripción:', error);
      toast.error(error.message || 'Error al generar la descripción');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const treeRef = doc(db, 'trees', tree.id);
      await updateDoc(treeRef, formData);

      await logAdminActivity('update', {
        treeId: tree.id,
        previousData: {
          name: tree.name,
          creator: tree.creator,
          email: tree.email,
          votes: tree.votes,
          views: tree.views,
          status: tree.status,
          description: tree.description
        },
        newData: formData,
        timestamp: new Date()
      });

      toast.success('Participante actualizado correctamente');
      onSuccess();
    } catch (error) {
      console.error('Error updating tree:', error);
      toast.error('Error al actualizar el participante');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary">Editar Participante</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Árbol
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Creador
            </label>
            <input
              type="text"
              name="creator"
              value={formData.creator}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Votos
              </label>
              <input
                type="number"
                name="votes"
                value={formData.votes}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visualizaciones
              </label>
              <input
                type="number"
                name="views"
                value={formData.views}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="pending">Pendiente</option>
              <option value="approved">Aprobado</option>
              <option value="rejected">Rechazado</option>
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Descripción (Generada por IA)
                </div>
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={isGeneratingDescription}
                className="text-primary hover:text-primary-dark text-sm flex items-center disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isGeneratingDescription ? 'animate-spin' : ''}`} />
                {isGeneratingDescription ? 'Generando...' : 'Generar descripción'}
              </button>
            </div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="La descripción se generará automáticamente al aprobar el árbol..."
            />
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg disabled:opacity-50"
            >
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}